import React, { useState, useEffect, useRef } from 'react';
import { X, Calendar, FileText, Link, MapPin } from 'lucide-react';

const BorderTunnelsMap = () => {
  const mapRef = useRef(null);
  const leafletMapRef = useRef(null);
  const markersRef = useRef([]);
  const [selectedTunnel, setSelectedTunnel] = useState(null);
  const [mapLoaded, setMapLoaded] = useState(false);

  // Real CSV data embedded directly in the component
  const tunnelData = [
    {
      id: 1,
      address: "902 3rd St., Calexico, California 92231",
      Latitude: 32.667456,
      Longitude: -115.485981,
      date: "December 2009",
      narrative: "Mexican military tipped off ICE agents in the U.S. about a tunnel for drug smuggling with an entry/exit point in a Calexico warehouse. A defendant in the case said someone known to him only as \"Harrison\" offered money to build a 5-foot by 7-foot tunnel between the warehouse and Mexico.",
      source: "https://www.documentcloud.org/documents/24740817-alvarez-peralta/"
    },
    {
      id: 2,
      address: "902 3rd St., Calexico, California 92231",
      Latitude: 32.667456,
      Longitude: -115.485981,
      date: "March 2016",
      narrative: "Homeland Security Investigations arrested a defendant who bought the property for $240,000, built a residence on it and continued drug smuggling operations with a tunnel. There were also two nearby stash houses.",
      source: "https://www.documentcloud.org/documents/24740843-cruz/"
    },
    {
      id: 3,
      address: "9948 Via De La Amistad, San Diego, California 92154",
      Latitude: 32.5534173,
      Longitude: -116.9304115,
      date: "November 2010",
      narrative: "Multiple defendants faced drug smuggling charges from their work at a warehouse in the Otay Mesa section of San Diego, located at 9948 Via De La Amistad, where they allegedly stored cannabis that they brought in through a cross-border tunnel in the property.",
      source: "https://www.documentcloud.org/documents/24740822-cunningham/"
    },
    {
      id: 4,
      address: "10145 Via De La Amistad, San Diego, California 92154",
      Latitude: 32.5519607,
      Longitude: -116.9282992,
      date: "April 2014",
      narrative: "Homeland Security Investigations started accumulating information about a cross-border underground tunnel between a mini storage facility in Tijuana, located about 800 feet south of the border, and an Otay Mesa warehouse at 10145 Via De La Amistad.",
      source: "https://www.documentcloud.org/documents/24740791-madrid/"
    },
    {
      id: 5,
      address: "2587 Otay Center Drive, San Diego, California 92154",
      Latitude: 32.5523093,
      Longitude: -116.9415187,
      date: "October 2015",
      narrative: "Homeland Security Investigations undercover agents started working with the eventual defendants in this case on cannabis smuggling operations out of this warehouse, located at 2587 Otay Center Drive. They worked on transporting the drugs to another warehouse that the agents were using for purposes of this operation. A search warrant at the Otay Center Drive warehouse led agents to a tunnel entrance that descended 32 feet underground and across the border to Mexico.",
      source: "https://www.documentcloud.org/documents/24740780-acosta/"
    }
  ];

  useEffect(() => {
    // Load Leaflet CSS and JS
    const loadLeaflet = async () => {
      // Add Leaflet CSS
      if (!document.querySelector('link[href*="leaflet"]')) {
        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/leaflet.css';
        document.head.appendChild(link);
      }

      // Load Leaflet JS
      if (!window.L) {
        const script = document.createElement('script');
        script.src = 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/leaflet.min.js';
        script.onload = initializeMap;
        document.head.appendChild(script);
      } else {
        initializeMap();
      }
    };

    const initializeMap = () => {
      if (!mapRef.current || leafletMapRef.current) return;

      // Initialize the map centered closer to the actual tunnel locations
      const map = window.L.map(mapRef.current, {
        center: [32.61, -116.2],
        zoom: 10,
        zoomControl: true
      });

      leafletMapRef.current = map;

      // Add tile layer (OpenStreetMap)
      window.L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '© OpenStreetMap contributors',
        maxZoom: 18
      }).addTo(map);

      // Group tunnels by location for handling duplicates and clusters
      const locationGroups = {};
      tunnelData.forEach(tunnel => {
        const key = `${tunnel.Latitude},${tunnel.Longitude}`;
        if (!locationGroups[key]) {
          locationGroups[key] = [];
        }
        locationGroups[key].push(tunnel);
      });

      // Add markers for each location group
      Object.values(locationGroups).forEach((group) => {
        const isMultiple = group.length > 1;
        const firstTunnel = group[0];
        
        // Custom icon showing count for multiple tunnels
        const iconHtml = isMultiple 
          ? `<div style="
               background: #dc2626;
               border: 3px solid white;
               border-radius: 50%;
               width: 36px;
               height: 36px;
               display: flex;
               align-items: center;
               justify-content: center;
               color: white;
               font-weight: bold;
               font-size: 14px;
               box-shadow: 0 2px 8px rgba(0,0,0,0.3);
               cursor: pointer;
             ">${group.length}</div>`
          : `<div style="
               background: #dc2626;
               border: 3px solid white;
               border-radius: 50%;
               width: 30px;
               height: 30px;
               display: flex;
               align-items: center;
               justify-content: center;
               color: white;
               font-weight: bold;
               font-size: 12px;
               box-shadow: 0 2px 8px rgba(0,0,0,0.3);
               cursor: pointer;
             ">●</div>`;

        const tunnelIcon = window.L.divIcon({
          html: iconHtml,
          className: 'custom-tunnel-marker',
          iconSize: isMultiple ? [36, 36] : [30, 30],
          iconAnchor: isMultiple ? [18, 18] : [15, 15]
        });

        const marker = window.L.marker([firstTunnel.Latitude, firstTunnel.Longitude], {
          icon: tunnelIcon
        }).addTo(map);

        // Create popup content for single or multiple tunnels
        let popupContent;
        
        if (isMultiple) {
          // For multiple tunnels at same location, create toggleable interface
          popupContent = `
            <div style="max-width: 350px; font-family: system-ui, -apple-system, sans-serif; line-height: 1.4;">
              <div style="margin-bottom: 12px; font-weight: 600; font-size: 16px; color: #1f2937;">
                ${group.length} Tunnels at This Location
              </div>
              <div style="margin-bottom: 12px;">
                <strong>Address:</strong> ${firstTunnel.address}
              </div>
              <div style="margin-bottom: 12px;">
                <label style="display: block; margin-bottom: 8px; font-weight: 600;">Select Case:</label>
                <select id="tunnel-select-${firstTunnel.Latitude}-${firstTunnel.Longitude}" 
                        onchange="window.updateTunnelInfo(this, ${JSON.stringify(group).replace(/"/g, '&quot;')})"
                        style="width: 100%; padding: 6px; border: 1px solid #d1d5db; border-radius: 4px; margin-bottom: 12px;">
                  ${group.map((tunnel, index) => 
                    `<option value="${index}">${tunnel.date}</option>`
                  ).join('')}
                </select>
              </div>
              <div id="tunnel-info-${firstTunnel.Latitude}-${firstTunnel.Longitude}">
                <div style="margin-bottom: 12px;">
                  <strong>Date:</strong> ${firstTunnel.date}
                </div>
                <div style="margin-bottom: 12px;">
                  <strong>Narrative:</strong> ${firstTunnel.narrative}
                </div>
                <div>
                  <a href="${firstTunnel.source}" target="_blank" rel="noopener noreferrer" 
                     style="color: #3b82f6; text-decoration: underline; cursor: pointer;">
                    Click here for source
                  </a>
                </div>
              </div>
            </div>
          `;
        } else {
          // For single tunnel, use simple format
          popupContent = `
            <div style="max-width: 300px; font-family: system-ui, -apple-system, sans-serif; line-height: 1.4;">
              <div style="margin-bottom: 12px;">
                <strong>Address:</strong> ${firstTunnel.address}
              </div>
              <div style="margin-bottom: 12px;">
                <strong>Date:</strong> ${firstTunnel.date}
              </div>
              <div style="margin-bottom: 12px;">
                <strong>Narrative:</strong> ${firstTunnel.narrative}
              </div>
              <div>
                <a href="${firstTunnel.source}" target="_blank" rel="noopener noreferrer" 
                   style="color: #3b82f6; text-decoration: underline; cursor: pointer;">
                  Click here for source
                </a>
              </div>
            </div>
          `;
        }

        marker.bindPopup(popupContent, {
          maxWidth: 400,
          className: 'custom-popup'
        });

        markersRef.current.push(marker);
      });

      // Fit map to show all markers
      if (tunnelData.length > 0) {
        const group = new window.L.featureGroup(markersRef.current);
        map.fitBounds(group.getBounds().pad(0.1));
      }

      setMapLoaded(true);
    };

    // Global function to update tunnel info in popup
    window.updateTunnelInfo = (selectElement, tunnelGroup) => {
      const selectedIndex = parseInt(selectElement.value);
      const selectedTunnel = tunnelGroup[selectedIndex];
      const infoDiv = document.getElementById(`tunnel-info-${selectedTunnel.Latitude}-${selectedTunnel.Longitude}`);
      
      if (infoDiv) {
        infoDiv.innerHTML = `
          <div style="margin-bottom: 12px;">
            <strong>Date:</strong> ${selectedTunnel.date}
          </div>
          <div style="margin-bottom: 12px;">
            <strong>Narrative:</strong> ${selectedTunnel.narrative}
          </div>
          <div>
            <a href="${selectedTunnel.source}" target="_blank" rel="noopener noreferrer" 
               style="color: #3b82f6; text-decoration: underline; cursor: pointer;">
              Click here for source
            </a>
          </div>
        `;
      }
    };

    loadLeaflet();

    // Cleanup
    return () => {
      if (leafletMapRef.current) {
        leafletMapRef.current.remove();
        leafletMapRef.current = null;
      }
      markersRef.current = [];
      delete window.updateTunnelInfo;
    };
  }, []);

  const closePopup = () => {
    setSelectedTunnel(null);
  };

  return (
    <div className="w-full h-screen bg-gray-100 p-4">
      <div className="max-w-7xl mx-auto h-full flex flex-col">
        <div className="mb-4">
          <h1 className="text-3xl font-bold text-gray-800 flex items-center gap-2">
            <MapPin className="text-red-600" />
            A sample of California's cross-border tunnels used for drug smuggling
          </h1>
          <p className="text-gray-600 mt-2">
            Data compiled by Luke Harold. Coding generated by Claude. 
            <p>Based on an analysis of federal court documents. Click on any red marker to view tunnel details. Last updated Sept. 25, 2025.</p>  
            <p>{tunnelData.length} locations displayed</p>
          </p>
        </div>
        
        <div className="flex-1 bg-white rounded-lg shadow-lg overflow-hidden">
          <div 
            ref={mapRef}
            className="w-full h-full"
            style={{ minHeight: '500px' }}
          />
          
          {!mapLoaded && (
            <div className="absolute inset-0 bg-gray-200 flex items-center justify-center">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                <p className="text-gray-600">Loading map...</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Detailed Modal Popup */}
      {selectedTunnel && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center p-6 border-b border-gray-200">
              <h2 className="text-2xl font-bold text-gray-800">
                Tunnel Location #{selectedTunnel.id}
              </h2>
              <button
                onClick={closePopup}
                className="text-gray-500 hover:text-gray-700 transition-colors p-2"
              >
                <X size={24} />
              </button>
            </div>
            
            <div className="p-6 space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <MapPin size={20} className="text-red-600 mt-1 flex-shrink-0" />
                    <div>
                      <h3 className="font-semibold text-gray-700 mb-1">Location</h3>
                      <p className="text-gray-600">{selectedTunnel.address}</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="w-5 h-5 bg-blue-600 rounded mt-1 flex-shrink-0"></div>
                    <div>
                      <h3 className="font-semibold text-gray-700 mb-1">Coordinates</h3>
                      <p className="text-gray-600 font-mono text-sm">
                        {selectedTunnel.Latitude}°, {selectedTunnel.Longitude}°
                      </p>
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <Calendar size={20} className="text-green-600 mt-1 flex-shrink-0" />
                    <div>
                      <h3 className="font-semibold text-gray-700 mb-1">Discovery Date</h3>
                      <p className="text-gray-600">{selectedTunnel.date}</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <Link size={20} className="text-purple-600 mt-1 flex-shrink-0" />
                    <div>
                      <h3 className="font-semibold text-gray-700 mb-1">Source</h3>
                      <p className="text-gray-600">{selectedTunnel.source}</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <FileText size={20} className="text-orange-600 mt-1 flex-shrink-0" />
                <div>
                  <h3 className="font-semibold text-gray-700 mb-2">Narrative</h3>
                  <p className="text-gray-600 leading-relaxed">{selectedTunnel.narrative}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BorderTunnelsMap;